import React, { type ReactElement, type ReactNode, useState } from 'react';
import clsx from 'clsx';
import { CloseButton, Popover, PopoverHeader } from 'reactstrap';
import type { PopoverProps } from 'reactstrap';
import styles from './index.module.scss';

export type PopoverHeaderProps = {
  onClose: () => void;
  title?: string;
};

export function ControlledPopoverHeader({ title, onClose }): ReactElement {
  return (
    <div
      className={
        title
          ? clsx(styles.popoverHeaderBg, styles.popoverHeaderWrapper)
          : styles.popoverHeaderWrapper
      }
    >
      {title && <PopoverHeader>{title}</PopoverHeader>}

      <CloseButton onClick={(): void => onClose()} />
    </div>
  );
}

export type ControlledPopoverProps = Pick<
  PopoverProps,
  'target' | 'placement' | 'popperClassName' | 'hideArrow'
> & {
  trigger?: string; // Defaults to click behaviour - useful for long popovers that should only close via the close button.
  title?: ReactNode | string;
  children?: ReactNode;
};

export default function ControlledPopover({
  target,
  placement,
  popperClassName,
  hideArrow,
  trigger,
  title,
  children
}: React.PropsWithChildren<ControlledPopoverProps>): ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggle = (): void => setIsOpen((prevValue) => !prevValue);
  const onClose = (): void => setIsOpen(false);

  return (
    <Popover
      isOpen={isOpen}
      toggle={(): void => toggle()}
      target={target}
      placement={placement}
      popperClassName={popperClassName}
      hideArrow={hideArrow}
      trigger={trigger}
    >
      <ControlledPopoverHeader title={title} onClose={onClose} />
      {children}
    </Popover>
  );
}
